import React, { useState } from "react";
import { Button } from "@repo/ui";
import {
  GiftTableProduct,
  removeProductFromGiftTable,
  updateProductQuantity,
} from "../../../redux/slices/giftTableSlice";
import { RootState } from "../../../redux/store";
import { useAppSelector, useAppDispatch } from "../../../hooks/useAppDispatch";
import Modal from "../../../components/Modal/Modal";
import EmptyPlaceholder from "../../../components/EmptyPlaceholder/EmptyPlaceholder";
import Carousel from "../../../components/Carousel/Carousel";
import LazyMyProductCard from "./LazyMyProductCard";
import placeholderImage from "../../../assets/placeholders/picture.webp";
import { Product } from "../../../services/productApiService";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_MyGiftTable_components_MyTableContent";

const MyTableContent: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const giftTable = useAppSelector((state: RootState) => state.giftTable);
  const dispatch = useAppDispatch();
  const [selectedProduct, setSelectedProduct] = useState<{
    local: GiftTableProduct;
    api: Product;
  } | null>(null);

  const closeModal = () => {
    setSelectedProduct(null);
  };

  const handleProductSelect = (product: {
    local: GiftTableProduct;
    api: Product;
  }) => {
    setSelectedProduct(product);
  };

  const handleUpdateQuantity = (productID: string, quantity: number) => {
    dispatch(updateProductQuantity({ productID, quantity }));
  };

  const handleRemoveProduct = (productID: string) => {
    dispatch(removeProductFromGiftTable(productID));
  };

  // Categorize products using the LOCAL data
  const physicalProducts = giftTable.products.filter((p) => p.isPhysical);
  const favoriteProducts = giftTable.products.filter(
    (p) => p.priority && !p.isPhysical
  );

  const categorizedProducts: { [key: string]: GiftTableProduct[] } = {};
  giftTable.products
    .filter((p) => !p.priority && !p.isPhysical)
    .forEach((product) => {
      const category = product.category || t("otherCategory");
      if (!categorizedProducts[category]) {
        categorizedProducts[category] = [];
      }
      categorizedProducts[category].push(product);
    });

  return (
    <div className="px-4 py-6">
      {giftTable.products.length === 0 ? (
        <EmptyPlaceholder
          title={t("emptyTitle")}
          description={t("emptyDescription")}
        />
      ) : (
        <>
          {/* Physical Products Section */}
          {physicalProducts.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xl font-bold mt-8 mb-4 text-primary">
                {t("physicalProductsTitle")}
              </h2>
              <div
                className="grid gap-8"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                }}
              >
                {physicalProducts.map((localProduct) => (
                  <LazyMyProductCard
                    key={localProduct.productID}
                    localProduct={localProduct}
                    onProductSelect={handleProductSelect}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Favorite Products Section */}
          {favoriteProducts.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xl font-bold mt-8 mb-4 text-primary">
                {t("favoritesTitle")}
              </h2>
              <div
                className="grid gap-8"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                }}
              >
                {favoriteProducts.map((localProduct) => (
                  <LazyMyProductCard
                    key={localProduct.productID}
                    localProduct={localProduct}
                    onProductSelect={handleProductSelect}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Categorized Products */}
          {Object.entries(categorizedProducts).map(([category, products]) => (
            <section key={category} className="mb-8">
              <h2 className="text-xl font-bold mt-8 mb-4 text-secondary">
                {category}
              </h2>
              <div
                className="grid gap-8"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                }}
              >
                {products.map((localProduct) => (
                  <LazyMyProductCard
                    key={localProduct.productID}
                    localProduct={localProduct}
                    onProductSelect={handleProductSelect}
                  />
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {/* Product Modal */}
      {selectedProduct && (
        <Modal
          isOpen={!!selectedProduct}
          onClose={closeModal}
          className="z-50 fixed inset-0 flex items-center justify-center"
        >
          <div className="bg-white p-6 rounded shadow-lg max-w-md w-full">
            <h2 className="text-center font-bold text-xl">
              {selectedProduct.api.title}
            </h2>
            <p className="text-center my-4">
              {selectedProduct.local.isPhysical
                ? t("modalPhysicalText")
                : t("modalNonPhysicalText")}
            </p>
            <div className="relative mt-4">
              {selectedProduct.api.pictures?.length > 0 ? (
                <Carousel>
                  {selectedProduct.api.pictures.map((picture, index) => (
                    <img
                      key={index}
                      src={picture}
                      alt={t("slideAlt", { index })}
                      className="w-full h-96 object-contain"
                    />
                  ))}
                </Carousel>
              ) : (
                <img
                  src={placeholderImage}
                  alt={t("noImage")}
                  className="w-full h-96 object-contain"
                />
              )}
            </div>
            <div className="w-full border p-4 mt-8">
              <div className="flex justify-between items-center">
                <span>{t("quantityLabel")}</span>
                <div className="flex items-center">
                  <Button
                    onClick={() =>
                      handleUpdateQuantity(
                        selectedProduct.local.productID,
                        selectedProduct.local.quantity - 1
                      )
                    }
                    label="-"
                    disabled={selectedProduct.local.taken > 0}
                  />
                  <span className="mx-4 font-bold">
                    {selectedProduct.local.quantity}
                  </span>
                  <Button
                    onClick={() =>
                      handleUpdateQuantity(
                        selectedProduct.local.productID,
                        selectedProduct.local.quantity + 1
                      )
                    }
                    label="+"
                  />
                </div>
              </div>
            </div>
            <Button
              label={t("removeButton")}
              onClick={() => {
                handleRemoveProduct(selectedProduct.local.productID);
                closeModal();
              }}
              className="mt-4 w-full bg-red-500 text-white"
              disabled={selectedProduct.local.taken > 0}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default withTranslations(LOCALE)(MyTableContent);
